import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import QrScanner from "qr-scanner";
import {
  codeDuQr,
  fetchByBadge,
  lierContact,
  type Contact,
} from "../contacts/contacts";
import { useEvent } from "../context/EventProvider";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ SCANNER UN BADGE ═══
 *
 * On vise le QR de son interlocuteur, et il entre dans le carnet.
 *
 * ⚠️ LA CAMÉRA N'EST PAS UN ACQUIS. Elle se refuse (le navigateur demande, on dit non),
 * elle manque (un ordinateur de bureau), et elle est INTERDITE hors HTTPS. Chacun de ces
 * trois cas a sa phrase : « ça ne marche pas » sur un écran noir laisse chercher une
 * panne là où il n'y a qu'une permission à donner. Et dans les trois, la saisie du code
 * imprimé reste offerte — c'est elle qui sauve la journée quand le badge est abîmé.
 *
 * ⚠️ ELLE S'ARRÊTE QUAND ON QUITTE L'ÉCRAN. Une caméra laissée ouverte garde le voyant
 * allumé sur le téléphone de quelqu'un, et c'est ce qui fait désinstaller une application.
 *
 * ⚠️ LE DÉCODAGE SE FAIT DANS UN FIL À PART (`qr-scanner`), pas dans la boucle de rendu.
 * Décoder trente images par seconde sur le fil principal fige l'interface pendant qu'on
 * vise — sur le seul écran du produit où l'on tient l'appareil à bout de bras.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
export function Scan() {
  const t = useT();
  const event = useEvent();
  const { me, unavailable } = useParticipation();
  const client = useQueryClient();

  const title = useText(
    "FO_MEETING_V2_SCAN_TITLE",
    fallbackOf("FO_MEETING_V2_SCAN_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_SCAN_SUBTITLE",
    fallbackOf("FO_MEETING_V2_SCAN_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_SCAN_HTML");
  const manuelShown = useFlagOr("FO_MEETING_V2_SCAN_MANUAL");

  const video = useRef<HTMLVideoElement | null>(null);
  const [camera, setCamera] = useState<
    "waiting" | "open" | "refused" | "missing" | "insecure"
  >("waiting");
  const [typed, setTyped] = useState("");
  /** La dernière personne ajoutée — la preuve que le geste a servi. */
  const [added, setAdded] = useState<Contact | null>(null);
  const [notFound, setNotFound] = useState<string | null>(null);

  const ready = !!event && typeof me?.id === "number";

  const addByCode = useMutation({
    mutationFn: async (lu: string) => {
      const code = codeDuQr(lu);
      const contact = await fetchByBadge(
        event!.domainId,
        event!.eventId,
        code,
      );
      if (!contact) {
        setNotFound(code);
        return null;
      }
      /* ⚠️ ON NE S'AJOUTE PAS SOI-MÊME. Viser son propre badge est le premier geste de
         quiconque découvre l'écran ; le serveur créerait un lien de soi à soi, et le
         carnet afficherait sa propre fiche sans que rien ne l'explique. */
      if (contact.id === me!.id) {
        setNotFound(null);
        throw new Error(t("OWN_BADGE", "C'est votre propre badge."));
      }
      await lierContact(event!.domainId, event!.eventId, me!.id, contact.id);
      return contact;
    },
    onSuccess: (contact) => {
      if (!contact) return;
      setNotFound(null);
      setAdded(contact);
      /* Le carnet vient de changer : il se relira quand on y retournera. */
      void client.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  /**
   * ⚠️ LE MÊME CODE NE SE REJOUE PAS EN BOUCLE. La caméra rend le QR trente fois par
   * seconde tant qu'on le vise : sans cette garde, c'est trente recherches et trente
   * écritures pour un seul badge. On retient le dernier lu, et on ne repart que
   * lorsqu'il change.
   */
  const last = useRef<string | null>(null);
  const handled = useRef<(lu: string) => void>(() => {});
  handled.current = (lu: string) => {
    if (!ready || addByCode.isPending || lu === last.current) return;
    last.current = lu;
    addByCode.mutate(lu);
  };

  useEffect(() => {
    const el = video.current;
    if (!el) return;

    /* ⚠️ HORS D'UN CONTEXTE SÛR, `getUserMedia` N'EXISTE MÊME PAS — ce n'est pas un
       refus, c'est une absence. Le dire à part évite de faire chercher une permission
       que le navigateur ne proposera jamais. */
    if (!window.isSecureContext) {
      setCamera("insecure");
      return;
    }

    const scanner = new QrScanner(el, (r) => handled.current(r.data), {
      /* Le cadre de visée que le composant dessine lui-même : on garde le nôtre. */
      highlightScanRegion: false,
      highlightCodeOutline: false,
      preferredCamera: "environment",
      maxScansPerSecond: 5,
    });

    let alive = true;
    scanner
      .start()
      .then(() => alive && setCamera("open"))
      .catch((error: unknown) => {
        if (!alive) return;
        /* `NotAllowedError` : refusée. `NotFoundError` : il n'y en a pas. Les deux
           appellent des phrases différentes — et la seconde n'a aucune solution côté
           permissions, ce qui rend le conseil « autorisez la caméra » absurde. */
        const name = (error as { name?: string })?.name;
        setCamera(
          name === "NotFoundError" || name === "OverconstrainedError"
            ? "missing"
            : "refused",
        );
      });

    return () => {
      alive = false;
      scanner.stop();
      scanner.destroy();
    };
  }, []);

  const sendTyped = (e: React.FormEvent) => {
    e.preventDefault();
    /* ⚠️ TOUS LES BLANCS, PAS SEULEMENT CEUX DES BORDS. Le code imprimé se lit par
       groupes (« 753 811 2269 ») et se tape comme il se lit ; le serveur, lui, le
       cherche à l'identique (#354). */
    const sent = typed;
    const code = sent.replace(/\s+/g, "");
    if (!code || !ready || addByCode.isPending) return;
    /* La saisie relance même sur le même code : c'est un geste explicite, à la
       différence de la caméra qui répète le sien. */
    last.current = null;
    /* ⚠️ LE CHAMP NE SE VIDE QU'APRÈS UN AJOUT. Un code introuvable y reste, pour
       corriger le chiffre fautif sans tout retaper ; et ce qu'on a retouché pendant
       la recherche n'est pas effacé par sa réponse. */
    addByCode.mutate(code, {
      onSuccess: (contact) => {
        if (contact) setTyped((current) => (current === sent ? "" : current));
      },
    });
  };

  const word = {
    waiting: t("CAMERA_OPENING", "Ouverture de la caméra"),
    open: t("AIM_AT_BADGE_QR", "Visez le QR du badge"),
    refused: t(
      "CAMERA_REFUSED",
      "La caméra est refusée. Autorisez-la dans votre navigateur, puis rechargez la page.",
    ),
    missing: t("CAMERA_MISSING", "Aucune caméra sur cet appareil."),
    insecure: t(
      "CAMERA_NEEDS_HTTPS",
      "La caméra n'est accessible qu'en HTTPS. Saisissez le code imprimé sur le badge.",
    ),
  }[camera];

  return (
    <section className="page">
      <div className="ttl" data-cfg="scanTitle">
        <InPlace settingKey="FO_MEETING_V2_SCAN_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_SCAN_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="scanTitle" />
      )}

      <div className="scan__v" data-cfg="scanCam">
        {/* ⚠️ LA VIDÉO EST TOUJOURS MONTÉE, même quand la caméra est refusée : le
            composant qui la pilote s'y accroche au premier rendu, et la démonter selon
            l'état lui retirerait son support avant qu'il ait pu répondre. */}
        <video ref={video} className="scan__f" playsInline muted />
        <span className="scan__c" aria-hidden="true" />
        <span className="scan__h" role="status">
          {word}
        </span>
      </div>

      {manuelShown && (
        <form
          className="f f--wide"
          data-cfg="scanManual"
          onSubmit={sendTyped}
        >
          <label htmlFor="scan-code">
            {t("OR_TYPE_BADGE_CODE", 'Ou saisissez le code imprimé sur le badge')}
          </label>
          <div className="scan__m">
            <input
              id="scan-code"
              type="text"
              value={typed}
              autoComplete="off"
              placeholder="Ex. 3565992975"
              onChange={(e) => setTyped(e.target.value)}
            />
            <Button
              type="submit"
              loading={addByCode.isPending}
              disabled={!typed.trim()}
            >
              {t("ADD", "Ajouter")}
            </Button>
          </div>
        </form>
      )}

      {/* ⚠️ CE QUI VIENT DE SE PASSER, DIT SUR PLACE. On ne quitte pas l'écran — on
          enchaîne les rencontres — donc rien d'autre ne prouve que le scan a abouti. */}
      {added && (
        <div className="card card--on" data-cfg="scanResult">
          <b>{added.name}</b>
          {added.fiche.slice(0, 2).map((field) => (
            <small key={field.key}>
              {field.label} : {field.value}
            </small>
          ))}
          <small>{t("ADDED_TO_CONTACTS", 'Ajouté à vos contacts')}</small>
        </div>
      )}

      {/* `unavailable` : sans identité lue (échec non-401), `pret` est faux et le scan
          est inerte. On le dit, au lieu d'ignorer chaque badge en silence. */}
      {unavailable && (
        <p className="hint" role="alert">
          {t(
            "SCAN_SESSION_UNREADABLE",
            "Votre session n'a pas pu être lue : le scan est indisponible. Rechargez la page.",
          )}
        </p>
      )}
      {notFound && (
        <p className="hint" role="alert">
          {t(
            "NO_PARTICIPANT_WITH_CODE",
            "Aucun participant de cet événement ne porte le code « $1 ».",
            [notFound],
          )}
        </p>
      )}
      {addByCode.isError && (
        <p className="hint" role="alert">
          {(addByCode.error as Error).message}
        </p>
      )}
    </section>
  );
}
